import * as React from 'react';

enum ESelectionActionTypes {
  SELECT_ALL,
  SELECT_NONE,
  SELECT_ARRAY,
  TOGGLE_ONE,
}

export type ISelectionState<T> =
  // all items selected
  | Readonly<{
      allSelected: true;
      excludedItems: Array<T>;
    }>
  // some items selected
  | Readonly<{
      allSelected: false;
      includedItems: Array<T>;
    }>;

type Readonly<T> = { readonly [K in keyof T]: T[K] };

type ISelectionAction<T> =
  | Readonly<{
      type: ESelectionActionTypes.SELECT_ALL;
    }>
  | Readonly<{
      type: ESelectionActionTypes.SELECT_NONE;
    }>
  | Readonly<{
      type: ESelectionActionTypes.SELECT_ARRAY;
      payload: { allSelected?: boolean; items: Array<T> };
    }>
  | Readonly<{
      type: ESelectionActionTypes.TOGGLE_ONE;
      payload: T;
    }>;

function createReducer<T>(identifyItem: (item: T) => string) {
  return (
    state: ISelectionState<T>,
    action: ISelectionAction<T>
  ): ISelectionState<T> => {
    const includedItems =
      ('includedItems' in state && state.includedItems) || undefined;
    const excludedItems =
      ('excludedItems' in state && state.excludedItems) || undefined;
    switch (action.type) {
      case ESelectionActionTypes.TOGGLE_ONE:
        if (!includedItems && !excludedItems) {
          // new array from single item
          if (state.allSelected) {
            return {
              allSelected: true,
              excludedItems: [action.payload],
            };
          } else {
            return {
              allSelected: false,
              includedItems: [action.payload],
            };
          }
        } else if (
          includedItems &&
          includedItems.map(identifyItem).includes(identifyItem(action.payload))
        ) {
          return {
            allSelected: false,
            // remove item
            includedItems: includedItems.filter(
              (item) => identifyItem(item) !== identifyItem(action.payload)
            ),
          };
        } else if (
          excludedItems &&
          excludedItems.map(identifyItem).includes(identifyItem(action.payload))
        ) {
          return {
            allSelected: true,
            // remove item
            excludedItems: excludedItems.filter(
              (item) => identifyItem(item) !== identifyItem(action.payload)
            ),
          };
        } else if (state.allSelected) {
          return {
            allSelected: true,
            // add item
            excludedItems: [...state.excludedItems, action.payload],
          };
        } else {
          return {
            allSelected: false,
            // add item
            includedItems: [...state.includedItems, action.payload],
          };
        }
      case ESelectionActionTypes.SELECT_ALL:
        return {
          allSelected: true,
          excludedItems: [],
        };
      case ESelectionActionTypes.SELECT_NONE:
        return {
          allSelected: false,
          includedItems: [],
        };
      case ESelectionActionTypes.SELECT_ARRAY:
        const allSelected =
          typeof action.payload.allSelected === 'boolean'
            ? action.payload.allSelected
            : state.allSelected;
        if (allSelected) {
          return {
            allSelected: true,
            excludedItems: action.payload.items,
          };
        } else {
          return {
            allSelected: false,
            includedItems: action.payload.items,
          };
        }
      default:
        throw new Error('missing or incorrect action');
    }
  };
}

export function useSelectionState<T>(identifyItem: (item: T) => string): {
  toggleOne: (item: T) => void;
  selectMany: (itemOrItems: Array<T>, allSelected?: boolean) => void;
  selectAll: () => void;
  selectNone: () => void;
  selection: ISelectionState<T>;
} {
  const [selection, dispatch] = React.useReducer<
    React.Reducer<ISelectionState<T>, ISelectionAction<T>>
  >(createReducer(identifyItem), { allSelected: false, includedItems: [] });

  const toggleOne = React.useCallback(
    (item: T): void => {
      dispatch({
        type: ESelectionActionTypes.TOGGLE_ONE,
        payload: item,
      });
    },
    [dispatch]
  );

  const selectMany = React.useCallback(
    (items: Array<T>, allSelected?: boolean): void => {
      dispatch({
        type: ESelectionActionTypes.SELECT_ARRAY,
        payload: { allSelected, items },
      });
    },
    [dispatch]
  );

  const selectAll = React.useCallback((): void => {
    dispatch({ type: ESelectionActionTypes.SELECT_ALL });
  }, [dispatch]);

  const selectNone = React.useCallback((): void => {
    dispatch({ type: ESelectionActionTypes.SELECT_NONE });
  }, [dispatch]);

  return {
    toggleOne,
    selectMany,
    selectAll,
    selectNone,
    selection,
  };
}
